import { apiClient, isSupabaseConfigured } from './_shared.js'

// ============================================
// TASKS (Block 16 — Task Planner)
// ============================================

export async function getTasks(orgId) {
  if (!isSupabaseConfigured) {
    return JSON.parse(localStorage.getItem('demo_tasks') || '[]')
  }
  if (!orgId) return []
  return await apiClient(`/tasks?orgId=${orgId}`)
}

export async function createTask(orgId, task) {
  if (!isSupabaseConfigured) {
    const tasks = JSON.parse(localStorage.getItem('demo_tasks') || '[]')
    const newTask = { 
      id: crypto.randomUUID(), 
      org_id: orgId, 
      ...task, 
      status: 'new', 
      created_at: new Date().toISOString(),
      assigned_profile: null,
      creator_profile: null
    }
    tasks.unshift(newTask)
    localStorage.setItem('demo_tasks', JSON.stringify(tasks))
    return newTask
  }

  return await apiClient('/tasks', {
    method: 'POST',
    body: JSON.stringify({ org_id: orgId, ...task })
  })
}

export async function updateTask(taskId, updates) {
  if (!isSupabaseConfigured) {
    const tasks = JSON.parse(localStorage.getItem('demo_tasks') || '[]')
    const idx = tasks.findIndex(t => t.id === taskId)
    if (idx !== -1) {
      tasks[idx] = { ...tasks[idx], ...updates }
      if (updates.status === 'done' && !tasks[idx].completed_at) {
        tasks[idx].completed_at = new Date().toISOString()
      }
      localStorage.setItem('demo_tasks', JSON.stringify(tasks))
      return tasks[idx]
    }
    return null
  }

  return await apiClient(`/tasks/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  })
}

/**
 * Если задача повторяющаяся (repeat_type) и была отмечена как done,
 * создаём следующую копию с новым due_date.
 * Возвращает новую задачу или null если повторение закончилось.
 */
export async function spawnNextRecurringTask(doneTask) {
  if (!doneTask.repeat_type || !doneTask.due_date) return null
  
  // Рассчитываем следующую дату
  const base = new Date(doneTask.due_date + 'T00:00:00')
  let next
  switch (doneTask.repeat_type) {
    case 'daily':
      next = new Date(base); next.setDate(next.getDate() + 1); break
    case 'weekly':
      next = new Date(base); next.setDate(next.getDate() + 7); break
    case 'monthly':
      next = new Date(base); next.setMonth(next.getMonth() + 1); break
    default:
      return null
  }
  
  const nextDate = next.toISOString().split('T')[0]
  
  // Проверяем repeat_end_date
  if (doneTask.repeat_end_date && nextDate > doneTask.repeat_end_date) return null
  
  // Создаём копию задачи с новой датой
  const newTask = {
    title: doneTask.title,
    description: doneTask.description || null,
    assigned_to: doneTask.assigned_to || null,
    created_by: doneTask.created_by || null,
    priority: doneTask.priority || 'normal',
    due_date: nextDate,
    due_time: doneTask.due_time || null,
    repeat_type: doneTask.repeat_type,
    repeat_end_date: doneTask.repeat_end_date || null,
    require_photo: doneTask.require_photo || false,
  }
  
  return createTask(doneTask.org_id, newTask)
}

export async function deleteTask(taskId) {
  if (!isSupabaseConfigured) {
    const tasks = JSON.parse(localStorage.getItem('demo_tasks') || '[]')
    localStorage.setItem('demo_tasks', JSON.stringify(tasks.filter(t => t.id !== taskId)))
    return true
  }

  await apiClient(`/tasks/${taskId}`, { method: 'DELETE' })
  return true
}

export async function getTaskComments(taskId) {
  if (!isSupabaseConfigured) {
    return JSON.parse(localStorage.getItem(`demo_task_comments_${taskId}`) || '[]')
  }

  return await apiClient(`/tasks/${taskId}/comments`)
}

export async function addTaskComment(taskId, authorId, content) {
  if (!isSupabaseConfigured) {
    const comments = JSON.parse(localStorage.getItem(`demo_task_comments_${taskId}`) || '[]')
    const newComment = {
      id: crypto.randomUUID(),
      task_id: taskId,
      author_id: authorId,
      content,
      created_at: new Date().toISOString(),
      author: { id: authorId, full_name: 'Пользователь' }
    }
    comments.push(newComment)
    localStorage.setItem(`demo_task_comments_${taskId}`, JSON.stringify(comments))
    return newComment
  }

  return await apiClient(`/tasks/${taskId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ author_id: authorId, content })
  })
}

export async function getTaskStats(orgId) {
  if (!isSupabaseConfigured) {
    const tasks = JSON.parse(localStorage.getItem('demo_tasks') || '[]')
    return {
      total: tasks.length,
      new: tasks.filter(t => t.status === 'new').length,
      in_progress: tasks.filter(t => t.status === 'in_progress').length,
      done: tasks.filter(t => t.status === 'done').length,
      overdue: tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'done' && t.status !== 'cancelled').length,
    }
  }
  if (!orgId) return { total: 0, new: 0, in_progress: 0, done: 0, overdue: 0 }

  return await apiClient(`/tasks/stats?orgId=${orgId}`)
}

export async function createTaskNotification(recipientId, taskId, taskTitle, senderName) {
  return sendTaskPush(recipientId, 'task_assigned', taskId, taskTitle, senderName)
}

/**
 * Универсальная функция отправки push-уведомления по задачам.
 * @param {string} recipientId - UUID получателя
 * @param {string} eventType - тип события: task_assigned | task_completed | task_approved | task_rejected | task_comment
 */
export async function sendBulkNotifications(notifications) {
  for (const n of notifications) {
    await apiClient('/notifications', {
      method: 'POST',
      body: JSON.stringify(n)
    })
  }
}
/**
 * @param {string} taskId - UUID задачи
 * @param {string} taskTitle - Название задачи
 * @param {string} senderName - Имя отправителя
 */
export async function sendTaskPush(recipientId, eventType, taskId, taskTitle, senderName) {
  if (!recipientId) return

  const EVENT_CONFIG = {
    task_assigned:  { title: 'Новая задача',     message: `${senderName} назначил вам: ${taskTitle}` },
    task_completed: { title: 'Задача выполнена',  message: `${senderName} выполнил: ${taskTitle}` },
    task_photo:     { title: 'Фотоотчёт',        message: `${senderName} прикрепил отчёт: ${taskTitle}` },
    task_approved:  { title: 'Отчёт одобрен',     message: `Задача одобрена: ${taskTitle}` },
    task_rejected:  { title: 'Отчёт отклонён',    message: `Задача возвращена в работу: ${taskTitle}` },
    task_comment:   { title: 'Комментарий',       message: `${senderName}: по задаче ${taskTitle}` },
  }

  const cfg = EVENT_CONFIG[eventType] || { title: 'Задачи', message: taskTitle }

  try {
    await apiClient('/notifications', {
      method: 'POST',
      body: JSON.stringify({
        profile_id: recipientId,
        type: eventType,
        title: cfg.title,
        message: cfg.message,
      })
    })
  } catch(e) {
    console.warn('sendTaskPush error:', e)
  }
}

export async function createSystemNotification(recipientId, title, message, type = 'system_alert') {
  try {
    await apiClient('/notifications', {
      method: 'POST',
      body: JSON.stringify({
        profile_id: recipientId,
        type: type,
        title: title,
        message: message,
      })
    })
  } catch(e) {
    console.warn('System notification error:', e)
  }
}
